import React from "react";
import { UnitProvider } from "./utils/units";
import { BrowserRouter, Navigate, Route, Routes, useLocation } from "react-router-dom";
import PublicLayout from "./layouts/PublicLayout";
import AdminLayout from "./layouts/AdminLayout";

// Public pages
import Home from "./pages/public/Home";
import Search from "./pages/public/Search";
import CompetitionList from "./pages/public/CompetitionList";
import Competitions from "./pages/public/Competitions";
import Matches from "./pages/public/Matches";
import CompetitionDetail from "./pages/public/CompetitionDetail";
import EventResults from "./pages/public/EventResults";
import MatchResults from "./pages/public/MatchResults";
import ShooterDetail from "./pages/public/ShooterDetail";
import OverallRankings from "./pages/public/Rankings";
import ClassGradeRankings from "./pages/public/ClassGradeRankings";
import RankingsByType from "./pages/public/RankingsByType";
import { CompMatches, CompRankings, CompResults } from "./pages/public/CompetitionSections";
import { adminSession } from "./api/client";
import { About, NotFound } from "./pages/public/StaticPages";

// Admin pages
import AdminLogin from "./pages/admin/Login";
import AdminDashboard from "./pages/admin/Dashboard";
import AdminCompetitions from "./pages/admin/Competitions";
import AdminCompetitors from "./pages/admin/Competitors";
import AdminMatches from "./pages/admin/Matches";
import ShotMapPage from "./pages/admin/ShotMapPage";
import ImportData from "./pages/admin/ImportData";
import ShotLogs from "./pages/admin/ShotLogs";
import TargetsPage from "./pages/admin/Targets";
import ImportHistoryPage from "./pages/admin/ImportHistory";
import SystemSettings from "./pages/admin/Settings";
import { AdminAuditLog } from "./pages/admin/SectionPages";
import AdminClasses from "./pages/admin/Classes";
import AdminAggregates from "./pages/admin/Aggregates";
import AdminUsers from "./pages/admin/Users";
import AdminClubs from "./pages/admin/Clubs";

// Admin routes require a signed-in session (Milestone: secure the admin portal).
// The session lives in localStorage and survives page reloads and trips out to
// the public site — `from` sends the user back to where they were headed.
function RequireAdmin({ children }: { children: React.ReactElement }) {
  const location = useLocation();
  if (!adminSession.valid()) {
    return <Navigate to="/admin/login" replace state={{ from: location.pathname + location.search }} />;
  }
  return children;
}

export default function App() {
  return (
    // Display-unit choice is site-wide, so the provider wraps everything.
    <UnitProvider>
    <BrowserRouter future={{ v7_startTransition: true, v7_relativeSplatPath: true }}>
      <Routes>
        {/* Public site */}
        <Route element={<PublicLayout />}>
          <Route path="/" element={<Home />} />
          <Route path="/competitions" element={<CompetitionList />} />
          {/* The competitions themselves, across every event. */}
          <Route path="/competition-list" element={<Competitions />} />
          {/* Every range across the portal, not just those inside one event. */}
          <Route path="/matches" element={<Matches />} />
          <Route path="/competitions/:id" element={<CompetitionDetail />} />
          <Route path="/competitions/:id/events/:eventId" element={<EventResults />} />
          <Route path="/competitions/:id/events/:eventId/matches/:matchId" element={<MatchResults />} />
          <Route path="/competitions/:id/matches" element={<CompMatches />} />
          <Route path="/competitions/:id/results" element={<CompResults />} />
          <Route path="/competitions/:id/rankings" element={<CompRankings />} />
          <Route path="/results" element={<CompResults />} />
          <Route path="/shooters/:sid" element={<ShooterDetail />} />
          <Route path="/strings/:stringId" element={<ShotMapPage />} />
          <Route path="/search" element={<Search />} />
          <Route path="/rankings" element={<OverallRankings />} />
          <Route path="/rankings/class" element={<ClassGradeRankings />} />
          <Route path="/rankings/:type" element={<RankingsByType />} />
          <Route path="/about" element={<About />} />
          <Route path="*" element={<NotFound />} />
        </Route>

        {/* Admin portal */}
        <Route path="/admin/login" element={<AdminLogin />} />
        <Route
          path="/admin"
          element={
            <RequireAdmin>
              <AdminLayout />
            </RequireAdmin>
          }
        >
          <Route index element={<AdminDashboard />} />
          <Route path="competitions" element={<AdminCompetitions />} />
          <Route path="competition-list" element={<Competitions />} />
          <Route path="matches-all" element={<Matches />} />
          <Route path="competitors" element={<AdminCompetitors />} />
          <Route path="matches" element={<AdminMatches />} />
          <Route path="aggregates" element={<AdminAggregates />} />
          <Route path="shot-logs" element={<ShotLogs />} />
          <Route path="targets" element={<TargetsPage />} />
          <Route path="import" element={<ImportData />} />
          <Route path="import-history" element={<ImportHistoryPage />} />
          <Route path="settings" element={<SystemSettings />} />
          <Route path="classes" element={<AdminClasses />} />
          <Route path="users" element={<AdminUsers />} />
          <Route path="clubs" element={<AdminClubs />} />
          <Route path="audit-log" element={<AdminAuditLog />} />

          {/* Detail views shared with the public site. Mounted here as well so an
              administrator drilling into a shooter, competition or target map
              stays inside the admin panel instead of being dropped onto the
              public site. usePortalLink() picks the right prefix. */}
          <Route path="search" element={<Search />} />
          <Route path="shooters/:sid" element={<ShooterDetail />} />
          <Route path="strings/:stringId" element={<ShotMapPage />} />
          <Route path="competitions/:id" element={<CompetitionDetail />} />
          <Route path="competitions/:id/events/:eventId" element={<EventResults />} />
          <Route path="competitions/:id/events/:eventId/matches/:matchId" element={<MatchResults />} />
          <Route path="competitions/:id/results" element={<CompResults />} />
          <Route path="competitions/:id/rankings" element={<CompRankings />} />
          <Route path="competitions/:id/matches" element={<CompMatches />} />

          {/* Unknown admin paths stay inside the admin shell rather than falling
              through to the public 404 with the public chrome. */}
          <Route path="*" element={<NotFound />} />
        </Route>
      </Routes>
    </BrowserRouter>
    </UnitProvider>
  );
}
